// ФАЙЛ: ZoneLayoutContext.tsx
// НАЗНАЧЕНИЕ: React-контекст зонной компоновки. Хранит реестр слотов
// (виджет → зона/приоритет/DOM-узел) и DOM-узлы самих зон, выполняет
// реконсиляцию: раскладывает узлы виджетов внутри зоны в порядке приоритета
// напрямую через appendChild (flex-стек, без ремаунта содержимого).

import type { ReactNode } from 'react'

import { OVERLAY_CONFIG } from '@shared/config'
// @ts-ignore — React берётся из окружения overlay-приложения.
import { createContext, useCallback, useContext, useMemo, useRef } from 'react'

import type { ZoneId, ZoneSlotOptions } from './types'

interface SlotRecord {
  widgetId: string
  zone: ZoneId
  priority: number
  node: HTMLDivElement
}

export interface ZoneLayoutContextValue {
  /** Регистрирует/обновляет слот виджета, возвращает его DOM-узел для портала. */
  registerSlot: (opts: ZoneSlotOptions) => HTMLDivElement
  /** Удаляет слот виджета (при unmount). */
  unregisterSlot: (widgetId: string) => void
  /** ZoneLayoutManager сообщает DOM-узел контейнера зоны (ref callback). */
  setZoneNode: (zone: ZoneId, node: HTMLDivElement | null) => void
}

const ZoneLayoutContext = createContext<ZoneLayoutContextValue | null>(null)

export function ZoneLayoutProvider({ children }: { children: ReactNode }) {
  // Реестр слотов и узлов зон в ref — прямые DOM-манипуляции не требуют ре-рендера.
  const slotsRef = useRef<Map<string, SlotRecord>>(new Map())
  const zoneNodesRef = useRef<Map<ZoneId, HTMLDivElement>>(new Map())

  // Реконсиляция одной зоны: сортировка слотов по priority (убыв.) и
  // переупорядочивание их узлов внутри контейнера зоны через appendChild.
  //
  // Мягкий лимит на зону (ZONE_MAX_CONCURRENT_WIDGETS): после сортировки в
  // DOM-контейнер зоны монтируются только top-N узлов (самые высокоприоритетные/
  // самые новые при равном приоритете). Излишние слоты не удаляются из
  // реестра и не теряют своё состояние (React-дерево внутри rec.node живое
  // через createPortal) — они просто открейваются от DOM (removeChild) и будут
  // автоматически домонтированы при следующем reconcileZone, когда освободится
  // место (выше того же unregisterSlot или смена приоритета).
  const reconcileZone = useCallback((zone: ZoneId) => {
    const zoneNode = zoneNodesRef.current.get(zone)
    if (!zoneNode) return
    const records: SlotRecord[] = []
    for (const rec of slotsRef.current.values()) {
      if (rec.zone === zone) records.push(rec)
    }
    records.sort((a, b) => b.priority - a.priority)

    const limit = OVERLAY_CONFIG.ZONE_MAX_CONCURRENT_WIDGETS
    const visible = records.slice(0, limit)
    const waiting = records.slice(limit)

    for (const rec of visible) {
      // appendChild перемещает уже присутствующий узел — итог: нужный порядок.
      zoneNode.appendChild(rec.node)
    }
    for (const rec of waiting) {
      // Сверху мягкого лимита — откреиваем узел от DOM, но оставляем в реестре —
      // состояние виджета внутри сохраняется, он просто не виден, пока не
      // освободится место в своей зоне.
      if (rec.node.parentNode === zoneNode) {
        zoneNode.removeChild(rec.node)
      }
    }
  }, [])

  const registerSlot = useCallback(
    (opts: ZoneSlotOptions): HTMLDivElement => {
      const priority = opts.priority ?? 0
      const existing = slotsRef.current.get(opts.widgetId)
      if (existing) {
        const prevZone = existing.zone
        existing.zone = opts.zone
        existing.priority = priority
        if (prevZone !== opts.zone) reconcileZone(prevZone)
        reconcileZone(opts.zone)
        return existing.node
      }
      const node = document.createElement('div')
      // Узел-слот не навязывает позиционирование — раскладку задаёт контейнер зоны.
      node.style.pointerEvents = 'auto'
      node.dataset.widgetId = opts.widgetId
      slotsRef.current.set(opts.widgetId, {
        widgetId: opts.widgetId,
        zone: opts.zone,
        priority,
        node,
      })
      reconcileZone(opts.zone)
      return node
    },
    [reconcileZone],
  )

  const unregisterSlot = useCallback((widgetId: string) => {
    const rec = slotsRef.current.get(widgetId)
    if (!rec) return
    slotsRef.current.delete(widgetId)
    if (rec.node.parentNode) rec.node.parentNode.removeChild(rec.node)
  }, [])

  const setZoneNode = useCallback(
    (zone: ZoneId, node: HTMLDivElement | null) => {
      if (node) {
        zoneNodesRef.current.set(zone, node)
        reconcileZone(zone)
      } else {
        zoneNodesRef.current.delete(zone)
      }
    },
    [reconcileZone],
  )

  const value = useMemo<ZoneLayoutContextValue>(
    () => ({ registerSlot, unregisterSlot, setZoneNode }),
    [registerSlot, unregisterSlot, setZoneNode],
  )

  return <ZoneLayoutContext.Provider value={value}>{children}</ZoneLayoutContext.Provider>
}

/** Доступ к контексту зон. null — если провайдер не смонтирован. */
export function useZoneLayoutContext(): ZoneLayoutContextValue | null {
  return useContext(ZoneLayoutContext)
}
